<template>
  <div class="flex max-md:scale-[0.85]" :class="[isExpanded ? 'gap-x-1 md:gap-x-2 lg:gap-x-3 xl:gap-x-3.5' : 'gap-0']" @drop="handleDrop" @dragover="handleDragOver" @dragenter="handleDragEnter">
    <GameCardItem
      :animateHover="animateCardHover"
      :cursor
      :class="[
        isExpanded ? 'expanded-card' : 'collapsed-card',
        canRearrange && isDragging && draggedCardId === card.id ? 'opacity-50 drop-target' : '',
        // canRearrange && isDragOver && dropTargetIndex === index ? 'drop-target' : '',
        isDragging && previewOrder.length ? 'preview-mode' : ''
      ]"
      v-for="(card, index) in orderedCards"
      :card="card"
      :key="card.id"
      @click="handleCardClick(card)"
      @dragstart="(event, draggedCard) => handleCardDragStart(event, draggedCard, index)"
      @dragend="handleCardDragEnd"
      :isActive="!isCardSelected(card)"
      :isDraggable="(canDragDrop || canRearrange) && !isCardSelected(card)"
      :data-card-index="index"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import GameCardItem from '@/components/game-play/GameCardItem.vue'
import type { CardDropEventT, CardT } from '@/types/Game'
import { useCardOrdering } from '@/composables/useCardOrdering'

const emit = defineEmits(['cardClick', 'cardDragStart', 'cardDragEnd', 'cardsReordered'])

const {
  cards,
  gameId,
  isExpanded = true,
  animateCardHover = false,
  disabledCards,
  cursor = 'default',
  canDragDrop = false,
  canRearrange = false
} = defineProps<{
  cards: CardT[]
  gameId?: string
  isExpanded?: boolean
  animateCardHover?: boolean
  disabledCards?: CardT[]
  cursor?: 'pointer' | 'default' | 'not-allowed' | 'grab' | 'grabbing'
  canDragDrop?: boolean
  canRearrange?: boolean
}>()

const { applyCardOrder } = useCardOrdering()

// Local state for card ordering
const cardOrder = ref<string[]>([])
const previewOrder = ref<string[]>([]) // Preview order during drag
const isDragging = ref(false)
const isDragOver = ref(false)
const draggedCardId = ref<string | null>(null)
const draggedCardIndex = ref<number | null>(null)
const dropTargetIndex = ref<number | null>(null)

// Helper function to compare arrays
const arraysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false
  const sortedA = [...a].sort()
  const sortedB = [...b].sort()
  return sortedA.every((val, index) => val === sortedB[index])
}

// Computed property for ordered cards
const orderedCards = computed(() => {
  // Use preview order during drag operations, otherwise use committed order
  const activeOrder = isDragging.value && previewOrder.value.length ? previewOrder.value : cardOrder.value

  if (!activeOrder.length) return cards

  // Map card IDs to actual cards and filter out invalid ones
  return activeOrder.map(cardId => cards.find(card => card && card.id === cardId)).filter((card): card is CardT => Boolean(card && card.id && card.category))
})

const handleCardClick = (card: CardT): void => {
  emit('cardClick', card)
}

const handleCardDragStart = (event: DragEvent, card: Partial<CardT>, index: number): void => {
  if (!canRearrange && !canDragDrop) return

  isDragging.value = true
  draggedCardId.value = card.id || null
  draggedCardIndex.value = index

  // Initialize preview order with current order
  previewOrder.value = [...cardOrder.value]

  // console.log('Drag start:', {
  //   cardId: card.id,
  //   index,
  //   currentOrder: cardOrder.value
  // })

  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = canRearrange ? 'move' : 'copy'
    const cardDragEvent: CardDropEventT = {
      card,
      isReorderOperation: canRearrange,
      sourceIndex: index
    }
    event.dataTransfer.setData('application/json', JSON.stringify(cardDragEvent))
  }

  emit('cardDragStart', card)
}

const handleCardDragEnd = (): void => {
  isDragging.value = false
  isDragOver.value = false
  draggedCardId.value = null
  draggedCardIndex.value = null
  dropTargetIndex.value = null

  // Clear preview order - if no drop occurred, this reverts to original order
  previewOrder.value = []

  emit('cardDragEnd')
}

const handleDragOver = (event: DragEvent): void => {
  if (!canRearrange) return

  event.preventDefault()
  event.dataTransfer!.dropEffect = 'move'

  // Continuously update preview as user drags over different positions
  const target = event.target as HTMLElement
  const cardElement = target.closest('[data-card-index]') as HTMLElement

  if (cardElement) {
    const targetIndex = parseInt(cardElement.dataset.cardIndex || '0')
    if (targetIndex !== dropTargetIndex.value) {
      dropTargetIndex.value = targetIndex
      updatePreviewOrder(targetIndex, event)
    }
  }
}

const handleDragEnter = (event: DragEvent): void => {
  if (!canRearrange) return

  event.preventDefault()
  isDragOver.value = true

  // Find the target index based on mouse position
  const target = event.target as HTMLElement
  const cardElement = target.closest('[data-card-index]') as HTMLElement

  if (cardElement) {
    const targetIndex = parseInt(cardElement.dataset.cardIndex || '0')
    dropTargetIndex.value = targetIndex

    // Update preview order
    updatePreviewOrder(targetIndex, event)
  }
}

// Update preview order to show what the arrangement would look like
const updatePreviewOrder = (targetIndex: number, event: DragEvent): void => {
  if (draggedCardIndex.value === null || !canRearrange) return

  const sourceIndex = draggedCardIndex.value
  let adjustedTargetIndex = targetIndex

  // Calculate the correct target position based on mouse position
  const target = event.target as HTMLElement
  const cardElement = target.closest('[data-card-index]') as HTMLElement

  if (cardElement) {
    const rect = cardElement.getBoundingClientRect()
    const mouseX = event.clientX
    const cardCenterX = rect.left + rect.width / 2

    // If mouse is to the right of center, insert after the target
    if (mouseX > cardCenterX) {
      adjustedTargetIndex = Math.min(targetIndex + 1, cardOrder.value.length)
    }
  }

  // console.log('UpdatePreviewOrder:', {
  //   sourceIndex,
  //   targetIndex,
  //   adjustedTargetIndex,
  //   baseOrder: cardOrder.value
  // })

  // Don't update if source and target are the same
  if (sourceIndex === adjustedTargetIndex) {
    previewOrder.value = [...cardOrder.value]
    return
  }

  // Create preview order based on the original committed order
  const newOrder = [...cardOrder.value]
  const [movedCard] = newOrder.splice(sourceIndex, 1)

  // Adjust target index if we're moving from left to right
  const finalTargetIndex = sourceIndex < adjustedTargetIndex ? adjustedTargetIndex - 1 : adjustedTargetIndex
  newOrder.splice(Math.max(0, finalTargetIndex), 0, movedCard)

  previewOrder.value = newOrder
}

const handleDrop = (event: DragEvent): void => {
  if (!canRearrange) return

  event.preventDefault()
  isDragOver.value = false

  try {
    const dragData = JSON.parse(event.dataTransfer?.getData('application/json') || '{}')

    if (!dragData.isReorderOperation || draggedCardIndex.value === null) {
      // console.log('Drop validation failed:', { dragData, draggedCardIndex: draggedCardIndex.value })
      return
    }

    // console.log('Drop detected:', {
    //   previewOrder: previewOrder.value,
    //   currentOrder: cardOrder.value,
    //   hasPreview: previewOrder.value.length > 0
    // })

    // Commit the preview order if it exists
    if (previewOrder.value.length > 0) {
      cardOrder.value = [...previewOrder.value]

      // Emit the reordered cards with null safety
      const reorderedCards = cardOrder.value.map(cardId => cards.find(card => card && card.id === cardId)).filter((card): card is CardT => Boolean(card && card.id && card.category))

      emit('cardsReordered', reorderedCards)
    } else {
      console.log('No preview order to commit')
    }
  } catch (error) {
    console.error('Error handling card drop:', error)
  } finally {
    dropTargetIndex.value = null
    previewOrder.value = [] // Clear preview after commit
  }
}

const isCardSelected = (card: CardT): boolean => {
  return Boolean(disabledCards?.some(selectedCard => selectedCard.id === card.id))
}

// Initialize card order when cards change
watch(
  () => cards,
  newCards => {
    if (newCards && newCards.length > 0) {
      // Filter out any invalid cards without IDs
      const validCards = newCards.filter(card => card && card.id)

      if (validCards.length === 0) return

      // Apply saved card order if gameId is provided and canRearrange is true
      if (gameId && canRearrange) {
        const orderedCards = applyCardOrder(validCards, gameId)
        // Ensure all cards in the ordered result have IDs
        cardOrder.value = orderedCards.filter(card => card && card.id).map(card => card.id)
      } else {
        const newOrder = validCards.map(card => card.id)
        // Only update if the cards have actually changed (not just reordered)
        if (!arraysEqual(newOrder, cardOrder.value)) {
          cardOrder.value = newOrder
        }
      }
    }
  },
  { immediate: true, deep: true }
)
</script>

<style scoped>
.collapsed-card {
  margin-left: -3.75rem !important;
  transition: margin-left 1s ease-in-out;
}

.drop-target {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(26, 165, 112, 0.3);
  border: 2px dashed #1aa570;
  transition: all 0.2s ease-in-out;
}

.drop-target.expanded-card {
  transform: translateY(-4px) scale(1.02);
}

.drop-target.collapsed-card {
  transform: translateY(-4px) scale(1.02);
}

.preview-mode {
  transition: all 0.15s ease-out;
}

.preview-mode:not(.opacity-50) {
  transform: translateY(-1px);
  filter: brightness(1.05);
}
</style>
